import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ReviewSubmitForm = ({ 
  formData, 
  selectedCategory, 
  files, 
  onSubmit, 
  onEdit, 
  isSubmitting 
}) => {
  const formatValue = (value) => {
    if (value === true) return 'Yes';
    if (value === false) return 'No';
    if (!value) return 'Not specified';
    return value;
  };

  const getBudgetRangeLabel = (value) => {
    const budgetOptions = {
      'under-5m': 'Under 5M UGX',
      '5m-15m': '5M - 15M UGX',
      '15m-30m': '15M - 30M UGX',
      '30m-50m': '30M - 50M UGX',
      '50m-100m': '50M - 100M UGX',
      'over-100m': 'Over 100M UGX',
      'discuss': 'Prefer to discuss'
    };
    return budgetOptions?.[value] || value;
  };

  const getPriorityLabel = (value) => {
    const priorityOptions = {
      'low': 'Low - Can wait 3+ months',
      'medium': 'Medium - Needed within 2-3 months',
      'high': 'High - Needed within 1 month',
      'urgent': 'Urgent - Needed ASAP'
    };
    return priorityOptions?.[value] || value;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString)?.toLocaleDateString('en-GB');
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-semibold text-foreground mb-2">Review & Submit</h2>
        <p className="text-muted-foreground">
          Please review your RFQ details before submitting
        </p>
      </div>
      <div className="space-y-6">
        {/* Service Category Summary */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Icon name={selectedCategory?.icon} size={20} className="text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">Service Category</h3>
                <p className="text-sm text-muted-foreground">{selectedCategory?.name}</p>
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(0)}>
              <Icon name="Edit" size={16} className="mr-2" />
              Edit
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">{selectedCategory?.description}</p>
        </div>

        {/* Project Details */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Project Details</h3>
            <Button variant="outline" size="sm" onClick={() => onEdit(1)}>
              <Icon name="Edit" size={16} className="mr-2" />
              Edit
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="font-medium text-foreground">Project Title:</p>
              <p className="text-muted-foreground">{formatValue(formData?.projectTitle)}</p>
            </div>
            <div>
              <p className="font-medium text-foreground">Priority Level:</p>
              <p className="text-muted-foreground">{getPriorityLabel(formData?.priorityLevel)}</p>
            </div>
            <div className="md:col-span-2">
              <p className="font-medium text-foreground">Project Description:</p>
              <p className="text-muted-foreground">{formatValue(formData?.projectDescription)}</p>
            </div>
          </div>
        </div>

        {/* Timeline & Budget */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Timeline & Budget</h3>
            <Button variant="outline" size="sm" onClick={() => onEdit(2)}>
              <Icon name="Edit" size={16} className="mr-2" />
              Edit
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Timeline</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium text-foreground">Start Date:</p>
                  <p className="text-muted-foreground">{formatDate(formData?.preferredStartDate)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Completion Date:</p>
                  <p className="text-muted-foreground">{formatDate(formData?.requiredCompletionDate)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Flexibility:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.timelineFlexibility)}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Budget</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium text-foreground">Budget Range:</p>
                  <p className="text-muted-foreground">{getBudgetRangeLabel(formData?.budgetRange)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Budget Type:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.budgetType)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Payment Preference:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.paymentPreference)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-foreground">Contact Information</h3>
            <Button variant="outline" size="sm" onClick={() => onEdit(3)}>
              <Icon name="Edit" size={16} className="mr-2" />
              Edit
            </Button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Primary Contact</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium text-foreground">Name:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.contactName)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Email:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.contactEmail)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Phone:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.contactPhone)}</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <h4 className="font-medium text-foreground">Company</h4>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="font-medium text-foreground">Company Name:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.companyName)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Industry:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.industry)}</p>
                </div>
                <div>
                  <p className="font-medium text-foreground">Company Size:</p>
                  <p className="text-muted-foreground">{formatValue(formData?.companySize)}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Uploaded Files */}
        {files?.length > 0 && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Supporting Documents</h3>
              <Button variant="outline" size="sm" onClick={() => onEdit(4)}>
                <Icon name="Edit" size={16} className="mr-2" />
                Edit
              </Button>
            </div>
            
            <div className="space-y-2">
              {files?.map((file, index) => (
                <div key={index} className="flex items-center space-x-3 p-2 bg-muted rounded">
                  <Icon name="File" size={16} className="text-muted-foreground" />
                  <span className="text-sm text-foreground">{file?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({(file?.size / 1024 / 1024)?.toFixed(2)} MB)
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Terms and Conditions */}
        <div className="bg-muted/50 border border-border rounded-lg p-6">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={20} className="text-primary mt-0.5" />
            <div className="text-sm">
              <h4 className="font-medium text-foreground mb-2">What happens next?</h4>
              <ul className="space-y-1 text-muted-foreground">
                <li>• You'll receive a confirmation email with your RFQ tracking number</li>
                <li>• Our team will review your requirements within 24 hours</li>
                <li>• We'll contact you to clarify any details if needed</li>
                <li>• You'll receive a detailed quote within 3-5 business days</li>
                <li>• We'll schedule a consultation call to discuss the proposal</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-center pt-6">
          <Button
            onClick={onSubmit}
            loading={isSubmitting}
            size="lg"
            className="px-8"
          >
            {isSubmitting ? 'Submitting RFQ...' : 'Submit RFQ Request'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmitForm;